import {
  Avatar,
  Button,
  Card,
  Col,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Tabs,
  Tooltip,
  Typography,
  theme,
  Skeleton
} from 'antd'
import React from 'react'
import { hexToRGBA } from '../../../../lib/utils'
import CustomRow from '../../../../reusable-components/CustomRow'
import ReactQuill from 'react-quill'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faEdit, faPlus, faRefresh, faTrash, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'
import AgiliteSkeleton from '../../../../reusable-components/AgiliteSkeleton'
import useClinicalNotesTemplates from '../hooks/useClinicalNotesTemplates'
import styled from 'styled-components'

const { Title, Text, Paragraph } = Typography

// Styled components for better UI
const TemplateCard = styled(Card)`
  margin-bottom: 12px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  overflow: hidden;

  .ant-card-head {
    border-bottom: none;
    background-color: #f0f7ff;
    padding: 12px 24px;
    min-height: auto;
  }

  .ant-card-head-title {
    flex: 1;
  }

  .ant-card-head-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .ant-card-body {
    background-color: #ffffff;
  }

  .ant-card-actions {
    background-color: #ffffff;
    border-top: 1px solid #f0f0f0;
  }
`

const ActionButton = styled(Button)`
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 80px;
  height: 32px;
  font-size: 14px;
`

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 16px;
  }
`

const ClinicalNotesTemplates = ({
  setValue,
  setModalOpen,
  debouncedNoteCapture,
  setUpdatingNotes,
  noteId,
  initialMode = 'browse'
}) => {
  const {
    isNew,
    editTarget,
    loading,
    dataPointsLoading,
    templates,
    formInitialValues,
    setState,
    newTemplateContent,
    handleCreateNotesTemplate,
    handleFetchTemplates,
    dataPointOptions,
    handleSelection,
    handleDeleteTemplate
  } = useClinicalNotesTemplates({
    setValue,
    setModalOpen,
    debouncedNoteCapture,
    setUpdatingNotes,
    noteId,
    initialMode
  })

  const { token } = theme.useToken()

  const renderTemplateList = () => {
    if (loading || dataPointsLoading) {
      return (
        <>
          {[1, 2].map((item) => (
            <div key={`skeleton-${item}`} style={{ marginBottom: 16 }}>
              <Card
                style={{
                  borderRadius: 8,
                  border: '1px solid #e8e8e8'
                }}
                headStyle={{
                  background: '#f0f7ff',
                  borderBottom: 'none'
                }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Skeleton.Avatar active size='default' shape='circle' style={{ marginRight: 10 }} />
                    <Skeleton.Input active size='small' style={{ width: 180 }} />
                  </div>
                }
                extra={
                  <Skeleton.Button active size='small' shape='circle' style={{ width: 24, minWidth: 24, height: 24 }} />
                }
                bodyStyle={{ padding: '16px 24px' }}
              >
                <Skeleton active paragraph={{ rows: 1, width: '100%' }} title={false} />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 8,
                    marginTop: 16,
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: 12
                  }}
                >
                  <Skeleton.Button active size='small' style={{ width: 80, height: 32 }} />
                  <Skeleton.Button active size='small' style={{ width: 80, height: 32 }} />
                </div>
              </Card>
            </div>
          ))}
        </>
      )
    }

    if (templates.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <FontAwesomeIcon
            icon={faPlus}
            style={{ fontSize: 32, color: hexToRGBA(token.colorPrimary, 0.6), marginBottom: 16 }}
          />
          <Title level={4}>No Templates Found</Title>
          <Paragraph type='secondary'>Create your first template to get started</Paragraph>
          <Button
            type='primary'
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => setState((prev) => ({ ...prev, isNew: true }))}
          >
            Create Template
          </Button>
        </div>
      )
    }

    return templates.map((template) => (
      <div key={template._id} style={{ marginBottom: 16 }}>
        <TemplateCard
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                style={{
                  backgroundColor: token.colorPrimary,
                  marginRight: 10
                }}
              >
                {template.name.charAt(0).toUpperCase()}
              </Avatar>
              <Text strong ellipsis style={{ fontSize: 16 }}>
                {template.name}
              </Text>
            </div>
          }
          extra={
            <Tooltip title='Delete'>
              <Popconfirm
                title='Delete this template?'
                description='This action cannot be undone.'
                onConfirm={() => handleDeleteTemplate(template._id)}
                okText='Yes'
                cancelText='No'
                okButtonProps={{ danger: true }}
              >
                <Button type='text' danger size='small' icon={<FontAwesomeIcon icon={faTrash} />} />
              </Popconfirm>
            </Tooltip>
          }
          actions={[
            <div key='actionBtns' style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '8px 24px' }}>
              <ActionButton
                key='edit'
                icon={<FontAwesomeIcon icon={faEdit} />}
                onClick={() => setState((prev) => ({ ...prev, editTarget: template }))}
              >
                Edit
              </ActionButton>
              <ActionButton
                key='select'
                type='primary'
                style={{ background: '#52c41a' }}
                icon={<FontAwesomeIcon icon={faCheckCircle} />}
                onClick={() => handleSelection(template)}
              >
                Select
              </ActionButton>
            </div>
          ]}
          bodyStyle={{ padding: '16px 24px' }}
        >
          <Paragraph style={{ margin: 0 }}>{template.description}</Paragraph>
        </TemplateCard>
      </div>
    ))
  }

  const renderEditor = () => {
    return (
      <Form initialValues={formInitialValues} layout='vertical' onFinish={handleCreateNotesTemplate}>
        <Row gutter={16}>
          <Col span={24} md={12}>
            <Form.Item
              rules={[{ required: true, message: 'Template requires a Name. Please revise.' }]}
              label={<Text strong>Template Name</Text>}
              name='name'
            >
              <Input placeholder='Enter template name' />
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              label={<Text strong>Data Points</Text>}
              name='dataPoints'
              help='Select data points for reference'
            >
              <Select
                style={{ width: '100%' }}
                showSearch
                optionFilterProp='label'
                options={dataPointOptions}
                mode='multiple'
                placeholder='Select data points'
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          rules={[{ required: true, message: 'Template requires a description. Please revise.' }]}
          label={<Text strong>Description</Text>}
          name='description'
        >
          <Input.TextArea rows={2} placeholder='Enter a brief description' />
        </Form.Item>

        <Form.Item
          label={<Text strong>Template Content</Text>}
          required
          help={!editTarget && !newTemplateContent ? 'Template requires content. Please revise.' : ''}
          validateStatus={!editTarget && !newTemplateContent ? 'warning' : ''}
        >
          <ReactQuill
            value={editTarget ? editTarget.content : newTemplateContent}
            onChange={(html) => {
              if (editTarget) {
                setState((prev) => ({ ...prev, editTarget: { ...prev.editTarget, content: html } }))
              } else {
                setState((prev) => ({ ...prev, newTemplateContent: html }))
              }
            }}
            style={{ minHeight: 200, marginBottom: 40 }}
          />
        </Form.Item>

        <Form.Item noStyle>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              onClick={() =>
                setState((prev) => ({
                  ...prev,
                  isNew: false,
                  editTarget: null,
                  newTemplateContent: ''
                }))
              }
            >
              Cancel
            </Button>
            <Button type='primary' style={{ background: token.colorSuccess }} loading={loading} htmlType='submit'>
              {isNew ? 'Create Template' : 'Save Changes'}
            </Button>
          </div>
        </Form.Item>
      </Form>
    )
  }

  return (
    <div>
      <div
        style={{
          width: '100%',
          padding: 8,
          background: hexToRGBA(token.colorPrimary, 0.2),
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          top: 0,
          left: 0
        }}
      >
        <h2>
          {isNew ? 'New Template' : `Clinical Notes Template - ${editTarget ? editTarget.name : 'Browse Templates'}`}
        </h2>
        <Button
          onClick={() => setModalOpen(false)}
          style={{ float: 'right', fontSize: 24, background: 'transparent', border: 'none' }}
        >
          <FontAwesomeIcon icon={faXmarkCircle} color={token.colorError} />
        </Button>
      </div>

      {isNew || editTarget ? (
        <Card style={{ marginTop: 64, borderRadius: 8 }} bordered={false}>
          {renderEditor()}
        </Card>
      ) : (
        <div style={{ marginTop: 24 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: '16px 0',
              borderBottom: '1px solid #f0f0f0',
              marginBottom: 20
            }}
          >
            <Button
              type='primary'
              icon={<FontAwesomeIcon icon={faRefresh} />}
              onClick={handleFetchTemplates}
              style={{
                marginRight: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                height: 40,
                borderRadius: 4
              }}
            >
              Refresh
            </Button>
            <Button
              type='primary'
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => setState((prev) => ({ ...prev, isNew: true }))}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                height: 40,
                borderRadius: 4
              }}
            >
              New Template
            </Button>
          </div>
          <div style={{ padding: '0 16px' }}>{renderTemplateList()}</div>
        </div>
      )}
    </div>
  )
}

export default React.memo(ClinicalNotesTemplates)
